// MACHINERY V2 (Stroje) — alarm strip (M2.1).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §4.3 (LOCKED).
// Reference impl: design_handoff_machinery_v3/machinery-live.jsx (AlarmStrip).
//
// A CONDITIONAL row: rendered only while an alarm is active, carrying
// code · message · since. It NEVER animates — no pulse, no flash, no
// transition. Colour plus position at the foot of the card is the whole
// signal; a blinking red band on a wall of 40 cards is noise, and a shop
// floor reads it as a UI fault. Do not add motion here in a later sub-phase.
//
// When the reading is last-known (conn stale/longstale) the "since" clause is
// prefixed accordingly, so nobody mistakes an old alarm for a live one.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import StatusIcon from "./StatusIcon";

export interface AlarmStripProps {
  code: string;
  message: string;
  since?: string | null;
  /** True when the telemetry is stale — flips "od" to "(naposledy)". */
  lastKnown?: boolean;
}

const AlarmStrip: React.FC<AlarmStripProps> = ({
  code,
  message,
  since = null,
  lastKnown = false,
}) => {
  const { translate } = useTranslation("myMachinery");

  return (
    <div
      style={{
        padding: "8px 12px",
        background: "color-mix(in oklab, var(--red) 14%, var(--card))",
        borderTop: "1px solid color-mix(in oklab, var(--red) 38%, var(--border))",
        display: "flex",
        alignItems: "center",
        gap: 10,
        color: "color-mix(in oklab, var(--red) 80%, var(--ink))",
        fontSize: 12,
      }}
    >
      <StatusIcon kind="alert" size={13} />
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontWeight: 700,
          letterSpacing: "0.02em",
          flexShrink: 0,
        }}
      >
        {code}
      </span>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          color: "var(--ink-2)",
        }}
      >
        {message}
      </span>
      {since && (
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10.5,
            color: "color-mix(in oklab, var(--red) 55%, var(--muted))",
            letterSpacing: "0.04em",
            textTransform: "lowercase",
            flexShrink: 0,
          }}
        >
          {lastKnown
            ? "(" + translate("myMachinery:v2.lastKnown") + ")"
            : translate("myMachinery:v2.sincePrefix")}{" "}
          {since}
        </span>
      )}
    </div>
  );
};

export default AlarmStrip;
